import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Drawer, Empty, Form, Input, Skeleton, Switch, message } from "antd";
import { BellOutlined, EditOutlined, MailOutlined, PhoneOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import { careService } from "services/careService";
import "./WorkspaceNotificationsPage.css";

const NOTIFICATION_TYPES = [
  { value: "MEDICATION_ADMIN", label: "Medication administration", description: "Medication administration activity and attention items." },
  { value: "ACCOUNT_ADMIN", label: "Account administration", description: "Workspace account and access updates." },
  { value: "STAFF_ADMIN", label: "Staff administration", description: "Care staff and workforce updates." },
  { value: "RESIDENT_ADMIN", label: "Resident administration", description: "Resident records and care administration updates." },
];

const DESTINATIONS = [
  { key: "primaryEmail", enabled: "primaryEmailEnabled", title: "Primary email", icon: MailOutlined, type: "email", placeholder: "notifications@example.com" },
  { key: "secondaryEmail", enabled: "secondaryEmailEnabled", title: "Secondary email", icon: MailOutlined, type: "email", placeholder: "backup@example.com" },
  { key: "primaryPhone", enabled: "primaryPhoneEnabled", title: "Primary phone", icon: PhoneOutlined, type: "tel", placeholder: "555 000 0000" },
  { key: "secondaryPhone", enabled: "secondaryPhoneEnabled", title: "Secondary phone", icon: PhoneOutlined, type: "tel", placeholder: "555 000 0000" },
];

const emptyRoute = (type) => ({
  type,
  primaryEmail: "", primaryEmailEnabled: false,
  secondaryEmail: "", secondaryEmailEnabled: false,
  primaryPhone: "", primaryPhoneEnabled: false,
  secondaryPhone: "", secondaryPhoneEnabled: false,
});

const nationalPhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
};

const formValues = (settings) => {
  const stored = new Map((settings?.notifications || []).map((route) => [route.type, route]));
  return {
    notifications: NOTIFICATION_TYPES.map(({ value }) => ({
      ...emptyRoute(value),
      ...(stored.get(value) || {}),
      type: value,
      primaryPhone: nationalPhone(stored.get(value)?.primaryPhone),
      secondaryPhone: nationalPhone(stored.get(value)?.secondaryPhone),
    })),
  };
};

const errorText = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

function NotificationCard({ definition, route }) {
  return (
    <article className="workspace-notification-type-card">
      <div className="workspace-notification-type-card__heading">
        <div><h3>{definition.label}</h3><p>{definition.description}</p></div>
      </div>
      <div className="workspace-notification-destinations">
        {DESTINATIONS.map((destination) => {
          const Icon = destination.icon;
          const enabled = Boolean(route?.[destination.enabled]);
          return (
            <div className="workspace-notification-destination" key={destination.key}>
              <Icon />
              <div><strong>{destination.title}</strong><small>{route?.[destination.key] || "Not configured"}</small></div>
              <span className={enabled ? "is-on" : "is-off"}>{enabled ? "On" : "Off"}</span>
            </div>
          );
        })}
      </div>
    </article>
  );
}

export default function WorkspaceNotificationsPage() {
  const { workspaceId } = useParams();
  const { user } = useAuth();
  const { selectedWorkspace } = useWorkspace();
  const actorId = user?.id || user?.userId || user?.uid || "";
  const [form] = Form.useForm();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const workspaceName = useMemo(
    () => selectedWorkspace?.id === workspaceId ? selectedWorkspace?.name : "this workspace",
    [selectedWorkspace, workspaceId],
  );

  const load = useCallback(async () => {
    if (!workspaceId || !actorId) return;
    setLoading(true);
    try {
      setSettings(await careService.workspaceNotifications.get(workspaceId, actorId));
    } catch (error) {
      message.error(errorText(error, "Unable to load notification settings."));
    } finally {
      setLoading(false);
    }
  }, [actorId, workspaceId]);

  useEffect(() => { load(); }, [load]);

  const openDrawer = () => {
    form.setFieldsValue(formValues(settings));
    setDrawerOpen(true);
  };

  const save = async (values) => {
    setSaving(true);
    try {
      const payload = {
        notifications: (values.notifications || []).map((route) => ({
          ...route,
          primaryPhone: route.primaryPhone ? `+1${nationalPhone(route.primaryPhone)}` : "",
          secondaryPhone: route.secondaryPhone ? `+1${nationalPhone(route.secondaryPhone)}` : "",
        })),
      };
      const updated = await careService.workspaceNotifications.update(workspaceId, payload, actorId);
      setSettings(updated);
      setDrawerOpen(false);
      message.success("Workspace notification settings updated.");
    } catch (error) {
      message.error(errorText(error, "Unable to update notification settings."));
    } finally {
      setSaving(false);
    }
  };

  const routes = new Map((settings?.notifications || []).map((route) => [route.type, route]));

  return (
    <div className="workspace-notifications-page">
      <header className="workspace-notifications-header">
        <div className="workspace-notifications-header__copy">
          <span className="workspace-notifications-header__icon"><BellOutlined /></span>
          <div><span className="workspace-notifications-eyebrow">WORKSPACE</span><h1>Notifications</h1><p>Choose where {workspaceName} receives important care and administration updates.</p></div>
        </div>
        <Button type="primary" icon={<EditOutlined />} onClick={openDrawer} disabled={loading}>Update settings</Button>
      </header>

      <section className="workspace-notifications-section">
        <div className="workspace-notifications-section__title">
          <div><h2>Notification types</h2><p>Configure separate email and phone recipients for each type.</p></div>
        </div>
        {loading ? <Skeleton active paragraph={{ rows: 8 }} /> : settings ? (
          <div className="workspace-notification-type-grid">
            {NOTIFICATION_TYPES.map((definition) => (
              <NotificationCard key={definition.value} definition={definition} route={routes.get(definition.value)} />
            ))}
          </div>
        ) : <Empty description="Notification settings could not be loaded" />}
      </section>

      <Drawer
        title="Update workspace notifications"
        width={760}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        className="workspace-notifications-drawer"
        footer={<div className="workspace-notifications-drawer__footer"><Button onClick={() => setDrawerOpen(false)}>Cancel</Button><Button type="primary" loading={saving} onClick={() => form.submit()}>Save settings</Button></div>}
      >
        <p className="workspace-notifications-drawer__intro">Set recipients and turn each email or phone destination on or off independently.</p>
        <Form form={form} layout="vertical" onFinish={save} initialValues={formValues(settings)}>
          <Form.List name="notifications">
            {(fields) => fields.map((field, index) => {
              const definition = NOTIFICATION_TYPES[index];
              return (
                <section className="workspace-notification-route-form" key={field.key}>
                  <div className="workspace-notification-route-form__heading">
                    <h3>{definition.label}</h3><p>{definition.description}</p>
                  </div>
                  <Form.Item name={[field.name, "type"]} hidden><Input /></Form.Item>
                  <div className="workspace-notification-route-fields">
                    {DESTINATIONS.map((destination) => {
                      const Icon = destination.icon;
                      return (
                        <div className="workspace-notification-route-field" key={destination.key}>
                          <div className="workspace-notification-route-field__label"><Icon /><strong>{destination.title}</strong><Form.Item name={[field.name, destination.enabled]} valuePropName="checked" noStyle><Switch checkedChildren="On" unCheckedChildren="Off" /></Form.Item></div>
                          <Form.Item
                            name={[field.name, destination.key]}
                            rules={destination.type === "email"
                              ? [{ type: "email", message: "Enter a valid email address." }]
                              : [{ validator: (_, value) => !value || /^\d{10}$/.test(nationalPhone(value))
                                ? Promise.resolve()
                                : Promise.reject(new Error("Enter a 10-digit US phone number.")) }]}
                          >
                            <Input
                              type={destination.type}
                              addonBefore={destination.type === "tel" ? "+1" : undefined}
                              placeholder={destination.placeholder}
                              autoComplete={destination.type}
                              inputMode={destination.type === "tel" ? "numeric" : undefined}
                            />
                          </Form.Item>
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </Form.List>
        </Form>
      </Drawer>
    </div>
  );
}
